import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import queryFn, { getDefault } from './query';
import PNV from '@/components/PositiveNegativeValue';
import TopWorst, { Period, Performance } from '../TopWorst';
import { InformationIcon, TooltipTextWrapper } from './styled';
import { CellLink } from '../DataTable';
import track, { useTracking } from 'react-tracking';
import { ROUTE_MARKET, ROUTE_MARKET_HOME } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { toLower } from 'lodash';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';

import { Text, Grid, useTheme, Box, Heading } from '@simplywallst/ui-core';
import Tooltip from '@/components/Tooltip';
import { persistedQueryConfig } from '@/utilities/persistQueryClient/utilities';

export const QUERY_KEY_DASHBOARD_INTERNATIONAL_MARKETS = [
  'DASHBOARD_INTERNATIONAL_MARKETS',
] as const;

enum Tabs {
  Top = 'Top Gainers',
  Worst = 'Top Losers',
}

interface Entity {
  return7D: number | null;
  return7DFormatted: string;
  return30D: number | null;
  return30DFormatted: string;
  return90D: number | null;
  return90DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  return3YrAbs: number | null;
  return3YrAbsFormatted: string;
  return5YrAbs: number | null;
  return5YrAbsFormatted: string;
}

interface Return {
  raw: null | number;
  formatted: string;
}
const selectReturn = (entity: Entity, period: Period): Return => {
  switch (period) {
    case '7D':
      return { raw: entity.return7D, formatted: entity.return7DFormatted };
    case '1M':
      return { raw: entity.return30D, formatted: entity.return30DFormatted };
    case '3M':
      return { raw: entity.return90D, formatted: entity.return90DFormatted };
    case '1Y':
      return {
        raw: entity.return1YrAbs,
        formatted: entity.return1YrAbsFormatted,
      };
    case '3Y':
      return {
        raw: entity.return3YrAbs,
        formatted: entity.return3YrAbsFormatted,
      };
    case '5Y':
      return {
        raw: entity.return5YrAbs,
        formatted: entity.return5YrAbsFormatted,
      };
    default:
      return {
        raw: null,
        formatted: '',
      };
  }
};

const InternationalMarkets = () => {
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const getTerm = useGlossaryTerms();
  const topGainersLosersTooltip = getTerm(`topGainersLosers`) ?? '';
  const { data = getDefault(), status } = useQuery({
    queryKey: QUERY_KEY_DASHBOARD_INTERNATIONAL_MARKETS,
    queryFn,
    ...persistedQueryConfig,
  });
  const getKeys = useCallback(
    (period: Period, perf: Performance) => {
      const { keys } = data;
      if (period === '7D' && perf === 'top') return keys.top.return7D;
      if (period === '7D' && perf === 'worst') return keys.worst.return7D;
      if (period === '1M' && perf === 'top') return keys.top.return30D;
      if (period === '1M' && perf === 'worst') return keys.worst.return30D;
      if (period === '3M' && perf === 'top') return keys.top.return90D;
      if (period === '3M' && perf === 'worst') return keys.worst.return90D;
      if (period === '1Y' && perf === 'top') return keys.top.return1YrAbs;
      if (period === '1Y' && perf === 'worst') return keys.worst.return1YrAbs;
      if (period === '3Y' && perf === 'top') return keys.top.return3YrAbs;
      if (period === '3Y' && perf === 'worst') return keys.worst.return3YrAbs;
      if (period === '5Y' && perf === 'top') return keys.top.return5YrAbs;
      if (period === '5Y' && perf === 'worst') return keys.worst.return5YrAbs;
      return [];
    },
    [data]
  );
  const getNewMarketPageCanonicalUrl = (key: string) => {
    const parse = JSON.parse(key);
    const isValidMarketArray = Array.isArray(parse) && parse.length >= 3;
    const market = isValidMarketArray ? toLower(parse[2]) : '';
    const pathRequest = {
      type: market ? ROUTE_MARKET : ROUTE_MARKET_HOME,
      payload: {
        market,
      },
    };
    return getPath(pathRequest);
  };
  return (
    <Box as="section" marginBottom="x4">
      <Box marginTop="x5">
        <Heading as="h1" typography="xSmall" color="whiteRGBA" opacity={0.7}>
          International Markets
        </Heading>
      </Box>
      <Grid gap="16px">
        <TopWorst
          topLabel={Tabs.Top}
          worstLabel={Tabs.Worst}
          disabled={status === 'pending'}
        >
          {({ period, tab }) =>
            getKeys(period, tab).map((n, i) => {
              const marketPageCanonicalUrl = getNewMarketPageCanonicalUrl(n);
              const { raw, formatted } = selectReturn(data.entities[n], period);
              return (
                <tr key={`top-worst-international-markets-${n}-${period}`}>
                  <td>
                    <CellLink
                      to={marketPageCanonicalUrl}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'country_list_item',
                          countryName: data.entities[n].countryName,
                          countryISO: data.entities[n].countryISO,
                          countryId: data.entities[n].countryId,
                          index: i,
                        })
                      }
                    >
                      <span>{data.entities[n].countryName}</span>
                    </CellLink>
                  </td>
                  <PNV as="td" value={raw}>
                    <CellLink
                      to={marketPageCanonicalUrl}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'country_list_item',
                          countryName: data.entities[n].countryName,
                          countryISO: data.entities[n].countryISO,
                          countryId: data.entities[n].countryId,
                          index: i,
                        })
                      }
                    >
                      {formatted}
                    </CellLink>
                  </PNV>
                </tr>
              );
            })
          }
        </TopWorst>
        <Grid
          alignItems="center"
          gap="8px"
          templateColumns="min-content max-content"
        >
          <Tooltip
            title={
              <TooltipTextWrapper>
                <Text textAlign="left" typography="tiny" color="spaceGray">
                  {topGainersLosersTooltip}
                </Text>
              </TooltipTextWrapper>
            }
            light
            placement="top"
            zIndex={99999}
          >
            <Grid>
              <InformationIcon fill={theme.color.whiteRGBA(0.5)} />
            </Grid>
          </Tooltip>
          <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
            How we calculate returns?
          </Text>
        </Grid>
      </Grid>
    </Box>
  );
};

export default track({
  type: 'track',
  subject: 'international_markets_worst_module',
})(InternationalMarkets);
